import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import api from "./apiClient";

import cookiesUtility from "../utils/cookies.utility";

import { HMIDeleteResource, HMIDeleteResourceThumbnail, HMIUpdateResource, HMIUploadResourceThumbnail, HMICreateResource, IResourceListItem } from "../interfaces/resources";

import useToast from "./useToast";

export const useGetResources = () => {
    const queryFn = async (): Promise<IResourceListItem[]> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.get("/resources/list/immersion/NO_SCHOOL_REQUIRED");
        return response.data;
    };

    return useQuery({
        queryKey: ["resources"],
        queryFn,
        retry: 0,
    });
};

export const useGetResourceDetails = (id: number | null) => {
    const queryFn = async (): Promise<IResourceListItem> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.get(`/resources/details/${id}`);
        return response.data;
    };

    return useQuery({
        queryKey: ["resource-details", id],
        queryFn,
        retry: 0,
        enabled: !!id,
    });
};

export const useCreateResource = () => {
    const { addToast } = useToast();
    const navigate = useNavigate();

    const mutationFn = async (data: HMICreateResource) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.post("/resources", data.payload, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsSaving(true);
        },
        onSuccess: (data, variables) => {
            addToast({
                type: 'success',
                title: 'Resource created successfully.',
            });

            variables.setThumbnailFile(null);

            navigate(`/resources/details/${data.id}`);
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsSaving(false);
        },
    });
};

export const useUpdateResource = () => {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    const mutationFn = async (data: HMIUpdateResource) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.patch(`/resources/${data.id}`, data.payload);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsSaving(true);
        },
        onSuccess: (_, variables) => {
            addToast({
                type: 'success',
                title: 'Resource updated successfully.',
            });

            queryClient.invalidateQueries({ queryKey: ["resource-details"] });
            variables.setThumbnailFile(null);
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsSaving(false);
        },
    });
};

export const useDeleteResource = () => {
    const { addToast } = useToast();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIDeleteResource) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.delete(`/resources/${data.id}`);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsDeleting(true);
        },
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Resource deleted successfully.',
            });

            queryClient.invalidateQueries({ queryKey: ["resources"] });

            navigate('/resources');
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsDeleting(false);
        },
    });
};

export const useUploadResourceThumbnail = () => {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    const mutationFn = async (data: HMIUploadResourceThumbnail) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.patch(`/resources/upload-thumbnail/${data.id}`,
            { file: data.file },
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsThumbnailUploading(true);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["resource-details"] });
            variables.setThumbnailFile(null);
        },
        onError: (error: any, variables) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });

            variables.setThumbnailFile(null);
        },
        onSettled: (_, __, variables) => {
            variables.setIsThumbnailUploading(false);
        },
    });
};

export const useDeleteResourceThumbnail = () => {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    const mutationFn = async (data: HMIDeleteResourceThumbnail) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.delete(`/resources/thumbnail/${data.id}`);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsThumbnailDeleting(true);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["resource-details"] });
            variables.setThumbnailFile(null);
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsThumbnailDeleting(false);
        },
    });
};